export const globalStyles = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  --white: #ffffff;
  --black: #000000;
  --gray-50: #fafafa;
  --gray-100: #f4f4f5;
  --gray-200: #e4e4e7;
  --gray-300: #d4d4d8;
  --gray-400: #a1a1aa;
  --gray-500: #71717a;
  --gray-600: #52525b;
  --gray-700: #3f3f46;
  --gray-800: #27272a;
  --gray-900: #18181b;
  --blue-600: #2563eb;
  --blue-500: #3b82f6;
  --blue-400: #60a5fa;
  --blue-300: #93bbfc;
  --blue-200: #bfdbfe;
  --blue-100: #dbeafe;
  --blue-50: #eff6ff;
  --green-600: #16a34a;
  --green-500: #22c55e;
  --green-50: #dcfce7;
  --red-600: #dc2626;
  --red-500: #ef4444;
  --red-50: #fef2f2;
  --purple-600: #9333ea;
  --purple-500: #a855f7;
  --purple-50: #faf5ff;
  --radius: 8px;
  --radius-lg: 12px;
  --radius-xl: 16px;
  --radius-2xl: 24px;
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
  --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  --transition-fast: 150ms ease;
  --transition-base: 300ms ease;
  --transition-slow: 500ms ease;
  --z-dropdown: 1000;
  --z-sticky: 1020;
  --z-fixed: 1030;
  --z-modal-backdrop: 1040;
  --z-modal: 1050;
  --z-popover: 1060;
  --z-tooltip: 1070;
}

/* Dark theme variables */
[data-theme="dark"] {
  --white: #18181b;
  --black: #ffffff;
  --gray-50: #18181b;
  --gray-100: #27272a;
  --gray-200: #3f3f46;
  --gray-300: #52525b;
  --gray-400: #71717a;
  --gray-500: #a1a1aa;
  --gray-600: #d4d4d8;
  --gray-700: #e4e4e7;
  --gray-800: #f4f4f5;
  --gray-900: #fafafa;
  --blue-600: #3b82f6;
  --blue-500: #60a5fa;
  --shadow-sm: 0 1px 2px 0 rgb(255 255 255 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(255 255 255 / 0.1), 0 2px 4px -2px rgb(255 255 255 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(255 255 255 / 0.1), 0 4px 6px -4px rgb(255 255 255 / 0.1);
  --shadow-xl: 0 20px 25px -5px rgb(255 255 255 / 0.1), 0 8px 10px -6px rgb(255 255 255 / 0.1);
}

html {
  scroll-behavior: smooth;
  font-size: 16px;
  scroll-padding-top: 100px;
}

body {
  font-family: 'Manrope', system-ui, -apple-system, sans-serif;
  background-color: var(--white);
  color: var(--gray-900);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  cursor: none;
  transition: background-color var(--transition-base), color var(--transition-base);
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}

::-webkit-scrollbar-track {
  background: var(--gray-100);
}

::-webkit-scrollbar-thumb {
  background: var(--gray-400);
  border-radius: 6px;
  border: 3px solid var(--gray-100);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--gray-500);
}

/* Selection */
::selection {
  background: var(--blue-500);
  color: var(--white);
}

/* Focus styles */
*:focus {
  outline: none;
}

*:focus-visible {
  outline: 2px solid var(--blue-600);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Utility classes */
.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
}

.section {
  padding: 8rem 2rem;
}

.section-alt {
  background: var(--gray-50);
}

.fade-in {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-in.visible {
  opacity: 1;
  transform: translateY(0);
}

.text-gradient {
  background: linear-gradient(135deg, var(--blue-600), var(--purple-600));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.shimmer {
  background: linear-gradient(90deg, var(--gray-200) 0%, var(--gray-100) 50%, var(--gray-200) 100%);
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}

/* Responsive utilities */
@media (max-width: 1280px) {
  .container {
    max-width: 1024px;
  }
}

@media (max-width: 1024px) {
  .container {
    max-width: 768px;
  }
}

@media (max-width: 768px) {
  .section {
    padding: 6rem 1rem;
  }
  
  .container {
    padding: 0 1rem;
  }
}

@media (max-width: 640px) {
  html {
    font-size: 14px;
  }
  
  .section {
    padding: 4rem 1rem;
  }
}

/* Print styles */
@media print {
  body {
    background: white;
    color: black;
  }
  
  .no-print {
    display: none !important;
  }
}
`;

export const componentStyles = {
  button: `
    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.875rem 1.75rem;
      border-radius: var(--radius);
      font-weight: 600;
      font-size: 1rem;
      text-decoration: none;
      transition: all var(--transition-base);
      cursor: pointer;
      border: none;
      position: relative;
      overflow: hidden;
      z-index: 1;
    }
    
    .btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .btn-primary {
      background: var(--black);
      color: var(--white);
    }
    
    .btn-primary::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: var(--blue-600);
      transition: left var(--transition-base);
      z-index: -1;
    }
    
    .btn-primary:hover:not(:disabled)::before {
      left: 0;
    }
    
    .btn-primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: var(--shadow-lg);
    }
    
    .btn-secondary {
      background: var(--white);
      color: var(--gray-700);
      border: 1px solid var(--gray-300);
    }
    
    .btn-secondary:hover:not(:disabled) {
      border-color: var(--gray-400);
      transform: translateY(-2px);
      box-shadow: var(--shadow-md);
    }
    
    .btn-ghost {
      background: transparent;
      color: var(--gray-700);
      padding: 0.5rem 1rem;
    }
    
    .btn-ghost:hover:not(:disabled) {
      background: var(--gray-100);
    }
    
    .btn-sm {
      padding: 0.625rem 1.25rem;
      font-size: 0.875rem;
    }
    
    .btn-lg {
      padding: 1rem 2rem;
      font-size: 1.125rem;
    }
    
    .btn-icon {
      padding: 0.75rem;
      width: 44px;
      height: 44px;
    }
  `,
  
  card: `
    .card {
      background: var(--white);
      border-radius: var(--radius-xl);
      padding: 2rem;
      border: 1px solid var(--gray-200);
      transition: all var(--transition-base);
      position: relative;
      overflow: hidden;
    }
    
    .card-hover:hover {
      box-shadow: var(--shadow-lg);
      transform: translateY(-4px);
      border-color: var(--blue-200);
    }
    
    .card-gradient {
      background: linear-gradient(135deg, var(--white), var(--gray-50));
    }
    
    .card-dark {
      background: var(--gray-900);
      color: var(--white);
      border-color: var(--gray-800);
    }
  `,
  
  input: `
    .input-group {
      margin-bottom: 1.5rem;
    }
    
    .input-label {
      display: block;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--gray-700);
      margin-bottom: 0.5rem;
    }
    
    .input {
      width: 100%;
      padding: 0.75rem 1rem;
      border: 1px solid var(--gray-300);
      border-radius: var(--radius);
      font-size: 1rem;
      background: var(--white);
      color: var(--gray-900);
      transition: all var(--transition-fast);
    }
    
    .input:hover {
      border-color: var(--gray-400);
    }
    
    .input:focus {
      border-color: var(--blue-500);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    .input-error {
      border-color: var(--red-500);
    }
    
    .input-error:focus {
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
    
    .input-helper {
      font-size: 0.875rem;
      color: var(--gray-500);
      margin-top: 0.5rem;
    }
    
    .input-error-message {
      font-size: 0.875rem;
      color: var(--red-600);
      margin-top: 0.5rem;
    }
    
    .textarea {
      min-height: 120px;
      resize: vertical;
    }
  `,
  
  badge: `
    .badge {
      display: inline-flex;
      align-items: center;
      padding: 0.375rem 0.75rem;
      border-radius: 50px;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    
    .badge-default {
      background: var(--gray-100);
      color: var(--gray-700);
    }
    
    .badge-primary {
      background: var(--blue-100);
      color: var(--blue-700);
    }
    
    .badge-success {
      background: var(--green-50);
      color: var(--green-600);
    }
    
    .badge-danger {
      background: var(--red-50);
      color: var(--red-600);
    }
  `,
  
  modal: `
    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      z-index: var(--z-modal-backdrop);
      opacity: 0;
      transition: opacity var(--transition-base);
    }
    
    .modal-backdrop.active {
      opacity: 1;
    }
    
    .modal {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) scale(0.9);
      background: var(--white);
      border-radius: var(--radius-xl);
      box-shadow: var(--shadow-2xl);
      z-index: var(--z-modal);
      max-width: 90vw;
      max-height: 90vh;
      overflow: auto;
      opacity: 0;
      transition: all var(--transition-base);
    }
    
    .modal.active {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    
    .modal-header {
      padding: 2rem;
      border-bottom: 1px solid var(--gray-200);
    }
    
    .modal-body {
      padding: 2rem;
    }
    
    .modal-footer {
      padding: 2rem;
      border-top: 1px solid var(--gray-200);
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
    }
  `
};
